import { waitFor } from '@/utils';
import { useCallback, useEffect, useState } from 'react';

// 현재 주소로 부터 해시 및 오픈 여부 확인
const getHashInfo = (id: string): { hash: string; hashStack: string[]; shouldOpen: boolean } => {
  const hash = window.location.hash;
  const hashStack = hash.substr(1).split('|');
  const shouldOpen = hashStack.some((dialogId) => dialogId === id);
  return {
    hash,
    hashStack,
    shouldOpen,
  };
};

const idMap = {};

type Hook = (param: { id: string }) => [boolean, (isOpened: boolean) => void, boolean];
// hook
const useModalHash: Hook = function ({ id }) {
  const [isOpened, setOpened] = useState<boolean>(isHashOpenedById(id));

  // 모달을 dynamic import 적용했을 때, 코드 분할된 파일 요청 여부 상태 값
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(isOpened);
  useEffect(() => {
    if (idMap[id]) {
      if (process.env.RUN_MODE !== 'production') {
        console.log(
          `[warning] Modal ID 에 중복이 있습니다. 중복된 ID 는 "${id}" 입니다. 잘못 설정된 것이 아닌지 다시 확인해 주세요.`,
        );
      }
    }
    idMap[id] = true;
    return () => void (idMap[id] = false);
  }, []);

  useEffect(() => {
    // 해시 변경을 통해 모달 오픈 / 닫기를 수행한다
    const onHashChange = (): void => setOpened(getHashInfo(id).shouldOpen);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [id]);

  useEffect(() => {
    // 모달이 한번 open 되면 코드 분할된 파일의 요청이 이루어지므로 이후에는 항상 true 이다
    if (isOpened) {
      setIsInitialLoad(true);
    }
  }, [isOpened]);

  const setState = useCallback(async (isOpened: boolean) => {
    const { shouldOpen: isExist } = getHashInfo(id);
    if (isOpened) {
      /**
       * 닫기와 열기가 같은 callstack 에서 연속적으로 일어나면 정상적으로 동작되지 않는다.
       * 열기 동작은 의도적으로 다음 tick 으로 넘기도록 한다.
       * 시간을 너무 짧게 설정해도 문제가 있는 걸로 보여 100ms 이후 동작하도록 함.
       */
      await waitFor(100);
      // 열때는 url 에 중복이 있는지 여부를 보고 해시에 아이디가 존재하지 않으면 추가한다
      const { hashStack } = getHashInfo(id);
      if (!isExist) {
        location.hash = '#' + [...hashStack, id].filter((id) => !!id).join('|'); // new Hash
      }
    } else if (!isOpened) {
      if (isExist) {
        // 닫을 때는 url 에 아이디가 존재하는지 여부를 보고 history back 을 한다.
        // url 에 아이디가 존재하지 않으면 history back 을 통해 온것이거나 이미 닫힌 모달이다.
        history.back();
      }
    }
  }, []);

  return [isOpened, setState, isInitialLoad];
};

export const isHashOpenedById = (id: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return getHashInfo(id).shouldOpen;
};

export default useModalHash;
